import { Box, Divider, TextField, Typography } from '@mui/material';
import { ClientTaxStatus, LegalEntity } from '../../../../common/types/client';
import { Controller } from 'react-hook-form';
import { EditClientStepProps } from '../../../types';
import { JSX } from 'react';
import {
  validateJuristicRegNumber,
  validationRegExpPatterns,
  validationsErrorMessages,
} from '../../../../common/lib/validations';
import AsyncFormSelect from '../../../../common/components/form/asyncFormSelect';
import DateTextField from '../../../../common/components/form/dateTextField';
import FormSelect from '../../../../common/components/form/formSelect';

/**
 * Legal entity basic information edit form
 */
const BasicInformationStep = ({
  control,
  watch,
}: EditClientStepProps<LegalEntity>): JSX.Element => {
  const taxStatus = watch('taxStatusID');

  return (
    <Box>
      <Box className="grid gap-8 lg:grid-cols-3 mb-8">
        <Controller
          name="legalname"
          control={control}
          rules={{
            required: { value: true, message: validationsErrorMessages.required },
            maxLength: { value: 50, message: validationsErrorMessages.maxLength },
            pattern: {
              value: validationRegExpPatterns.companyNamePattern,
              message: validationsErrorMessages.companyName,
            },
          }}
          render={({ field, fieldState: { error } }): JSX.Element => (
            <TextField
              {...field}
              label="Legal entity name *"
              placeholder="e.g. ABC Holdings"
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
        <Controller
          name="taxStatusID"
          control={control}
          rules={{
            required: { value: true, message: validationsErrorMessages.required },
            min: { value: 1, message: validationsErrorMessages.required },
          }}
          render={({ field, fieldState: { error } }): JSX.Element => (
            <AsyncFormSelect
              {...field}
              label="Tax status *"
              helperText={error?.message}
              error={Boolean(error)}
              optionsEndpoint="/TaxStatuss?TypeID=2"
            />
          )}
        />
        <Box />
        <Controller
          name="dateEstablished"
          control={control}
          rules={{
            pattern: {
              value: validationRegExpPatterns.datePattern,
              message: validationsErrorMessages.date,
            },
          }}
          render={({ field, fieldState: { error } }): JSX.Element => (
            <DateTextField
              {...field}
              allowEmptyFormatting={false}
              label="Date established"
              placeholder="e.g. 1972/11/28"
              error={!!error}
              helperText={error?.message || 'YYYY/MM/DD'}
            />
          )}
        />
        <Controller
          name="regNumber"
          control={control}
          rules={{
            required: { value: true, message: validationsErrorMessages.required },
            maxLength: { value: 50, message: validationsErrorMessages.maxLength },
            validate: (value: string): boolean | string => {
              const error = 'Please check the format of the registration number';
              return validateJuristicRegNumber(value, taxStatus) || error;
            },
          }}
          render={({ field, fieldState: { error } }): JSX.Element => (
            <TextField
              {...field}
              label="Registration number *"
              placeholder={
                taxStatus === ClientTaxStatus.company
                  ? 'e.g. 2023/123456/12'
                  : 'e.g. IT/020954/2014'
              }
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
      </Box>

      <Typography variant="h6">Additional info</Typography>
      <Divider />

      <Box className="mt-8 grid gap-8 lg:grid-cols-3">
        <Controller
          name="vatNumber"
          control={control}
          render={({ field, fieldState: { error } }): JSX.Element => (
            <TextField
              {...field}
              fullWidth
              type="number"
              label="VAT number"
              placeholder="e.g. 0123456789"
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
        <Controller
          name="taxNumber"
          control={control}
          render={({ field, fieldState: { error } }): JSX.Element => (
            <TextField
              {...field}
              label="TAX number"
              placeholder="e.g. 4123456789"
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
        <Controller
          name="language"
          control={control}
          render={({ field, fieldState: { error } }): JSX.Element => (
            <FormSelect
              {...field}
              label="Language"
              error={Boolean(error)}
              options={[
                { id: 'English', note: 'English' },
                { id: 'Afrikaans', note: 'Afrikaans' },
              ]}
            />
          )}
        />
      </Box>
    </Box>
  );
};

export default BasicInformationStep;
